import {CODE_PERMISSIONS, CodePermissionKey, PermissionScope} from './permissions';

export const ROLES = {
  // Internal roles (for your company's employees)
  INTERNAL_ADMIN: 'internal:admin',
  INTERNAL_ACCOUNTING: 'internal:accounting',
  INTERNAL_ANALYST: 'internal:analyst',

  // Client roles (for client company admins/staff)
  CLIENT_ADMIN: 'client:admin',
  CLIENT_ELIGIBILITY: 'client:eligibility',
  CLIENT_ACCOUNTING: 'client:accounting',

  // Group roles (for individual group admins/staff)
  GROUP_ADMIN: 'group:admin',
  GROUP_ELIGIBILITY: 'group:eligibility',
  GROUP_ACCOUNTING: 'group:accounting',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * Scope categorisation for both `Role` and `Permission`. Mirrors the Prisma
 * `RoleScope` enum and is intentionally identical to `PermissionScope` —
 * the same set of buckets gates assignment in both directions.
 */
export type RoleScope = PermissionScope;

export type SystemRoleDefinition = {
  readonly key: string;
  readonly name: string;
  readonly scope: RoleScope;
  readonly description: string;
  readonly permissions: readonly CodePermissionKey[];
};

/**
 * Eligibility capability keys. Every internal role holds these by default —
 * pre-permissions, ANY internal profile passed the eligibility gates — so
 * the checks in `eligibilityAccess.ts` can apply uniformly without an
 * internal-profile bypass while preserving existing access.
 */
const eligibilityPermissions: readonly CodePermissionKey[] = [
  CODE_PERMISSIONS.eligibility.view.key,
  CODE_PERMISSIONS.eligibility.update.key,
];

/**
 * Member PHI visibility (full SSN + DOB). Pre-wiring, every role that could
 * reach member search/details saw these fields (masking was cosmetic), so all
 * eligibility-holding roles keep them to preserve parity. Trimmable per role
 * in the admin UI now that the API redacts server-side.
 */
const memberPhiPermissions: readonly CodePermissionKey[] = [
  CODE_PERMISSIONS.phi.viewMemberSsnFull.key,
  CODE_PERMISSIONS.phi.viewMemberDob.key,
];

/**
 * Baseline held by every INTERNAL role. Pre-permissions, the CBS queue and
 * eligibility gates passed for ANY internal profile, so all internal roles
 * keep them to preserve parity. INTERNAL-only: never spread this into
 * client/group role bundles (it carries INTERNAL-scoped keys).
 */
const internalBaselinePermissions: readonly CodePermissionKey[] = [
  ...eligibilityPermissions,
  ...memberPhiPermissions,
  CODE_PERMISSIONS.rfc.cbsQueue.key,
];

/**
 * Member action keys enforced on member add/edit/terminate/export endpoints.
 * Pre-wiring these actions were open to any eligibility holder, so both admin
 * and eligibility client roles keep them for parity. Internal admins hold
 * them too so the internal portal can gate those surfaces on permissions
 * instead of hiding them.
 */
const memberActionPermissions: readonly CodePermissionKey[] = [
  CODE_PERMISSIONS.members.add.key,
  CODE_PERMISSIONS.members.edit.key,
  CODE_PERMISSIONS.members.terminate.key,
  CODE_PERMISSIONS.members.exportCsv.key,
  CODE_PERMISSIONS.members.editCoverage.key,
  CODE_PERMISSIONS.members.editDependents.key,
];

/**
 * User-management action keys. Client admins already hold these; internal
 * admins get them too so the internal portal can gate invite/edit/archive
 * on permissions instead of letting every internal profile through.
 */
const userManagementActionPermissions: readonly CodePermissionKey[] = [
  CODE_PERMISSIONS.userManagement.inviteUser.key,
  CODE_PERMISSIONS.userManagement.editUserBasic.key,
  CODE_PERMISSIONS.userManagement.editGroupAccess.key,
  CODE_PERMISSIONS.userManagement.archiveUser.key,
  CODE_PERMISSIONS.userManagement.generateRegistrationCodes.key,
  CODE_PERMISSIONS.userManagement.revokeRegistrationCodes.key,
];

const internalAdminPermissions: readonly CodePermissionKey[] = [
  CODE_PERMISSIONS.payroll.debug.key,
  CODE_PERMISSIONS.payroll.overrideTestUpload.key,
  CODE_PERMISSIONS.rfc.editProcessing.key,
  CODE_PERMISSIONS.onboarding.dryRun.key,
  CODE_PERMISSIONS.tenant.accessAll.key,
  CODE_PERMISSIONS.permissions.manage.key,
  CODE_PERMISSIONS.roles.manage.key,
  CODE_PERMISSIONS.userManagement.editUserRole.key,
  CODE_PERMISSIONS.userManagement.manageExternalUsers.key,
  ...userManagementActionPermissions,
  CODE_PERMISSIONS.claimsTracking.view.key,
  CODE_PERMISSIONS.audit.view.key,
  CODE_PERMISSIONS.tickets.manageInternal.key,
  CODE_PERMISSIONS.clients.manageFeatures.key,
  CODE_PERMISSIONS.internalConfig.manage.key,
  CODE_PERMISSIONS.groups.edit.key,
  CODE_PERMISSIONS.carrierReconciliation.manage.key,
  // Intake package completeness report (Reports & Analytics → Intake).
  CODE_PERMISSIONS.reports.viewIntake.key,
  // Enrollment section (enroll-site monitor, site builder, submissions log).
  CODE_PERMISSIONS.enrollment.view.key,
  CODE_PERMISSIONS.enrollment.manage.key,
  // Groups section (group pipeline, pipeline builder).
  CODE_PERMISSIONS.groupPipeline.view.key,
  CODE_PERMISSIONS.groupPipeline.manage.key,
  // Account document upload is permission gated on the internal portal too
  // (the tenant.access_all bypass doesn't apply — see
  // `assertPortalActionPermission`), so internal admins hold the key
  // explicitly to keep the Documents-tab upload button.
  CODE_PERMISSIONS.accounts.uploadDocuments.key,
  // The internal portal's Agents nav item is gated on this key (same key the
  // client portal uses for its Brokers page), so internal admins hold it
  // explicitly to keep the tab visible — untoggle per role in the admin UI.
  CODE_PERMISSIONS.pageAccess.viewBrokers.key,
  ...memberActionPermissions,
  // CBS handoff email audience. Pre-permissions only the retired
  // `internal:admin-cbs` role received it; `internal:admin` holds the key
  // now — trim per role in the admin UI if that's ever undesirable.
  CODE_PERMISSIONS.notifications.cbsRfcHandoff.key,
  // Eligibility for the ticket-reached-Clever-Benefits email. Delivery is
  // opt-in on top of this: the TICKET_CBS_REVIEW notification setting is off
  // by default, so holders only receive it after enabling it themselves.
  CODE_PERMISSIONS.tickets.cbsSubmissionAlerts.key,
  CODE_PERMISSIONS.notifications.manageOwn.key,
  ...internalBaselinePermissions,
];

/**
 * Baseline permission set for `client:admin`. Mirrors the pre-refactor
 * role-string gates so behavior is preserved when these become DB edges.
 */
const clientAdminPermissions: readonly CodePermissionKey[] = [
  CODE_PERMISSIONS.tickets.triage.key,
  // Escalation (dev-request review / forward-to-CB) is an admin capability;
  // formerly the sole distinguishing key of the retired client:super-admin role.
  CODE_PERMISSIONS.tickets.escalationReview.key,
  // `tickets.submission_alerts` is deliberately NOT in this bundle: new-ticket
  // alerts target the tenant's designated ticket contact(s) only (e.g. QTM's
  // primary contact), not every admin. Grant it via a dedicated role in the
  // role editor.
  CODE_PERMISSIONS.eligibility.view.key,
  CODE_PERMISSIONS.eligibility.update.key,
  ...memberPhiPermissions,
  CODE_PERMISSIONS.vip.manage.key,
  CODE_PERMISSIONS.externalTpa.viewData.key,
  CODE_PERMISSIONS.brokers.viewAs.key,
  CODE_PERMISSIONS.accounts.admin.key,
  CODE_PERMISSIONS.groups.adminAccess.key,
  CODE_PERMISSIONS.notifications.clientAdminLevel.key,
  CODE_PERMISSIONS.notifications.manageOwn.key,
  CODE_PERMISSIONS.rfc.viewPepm.key,
  // Row-level RFC read access (details, notes, downloads). Pre-permissions
  // only client admins could open individual RFCs, so only this role holds
  // it by default.
  CODE_PERMISSIONS.rfc.view.key,
  // Action-level write keys for RFC/prospect mutations — the page_access.*
  // view keys alone grant read access only.
  CODE_PERMISSIONS.rfc.edit.key,
  CODE_PERMISSIONS.prospects.edit.key,
  CODE_PERMISSIONS.payroll.clientAccess.key,
  CODE_PERMISSIONS.workflow.approveRfc.key,
  // MSA/CBS workflow actions the admin review UI exposes — enforced
  // server-side in rfcRouter/accountsRouter.
  CODE_PERMISSIONS.workflow.sendMsaViaDocusign.key,
  CODE_PERMISSIONS.workflow.approveSendToCbs.key,
  CODE_PERMISSIONS.workflow.editRfcInternalNotes.key,
  CODE_PERMISSIONS.pageAccess.viewRfcQueue.key,
  CODE_PERMISSIONS.pageAccess.viewProspects.key,
  // page_access.* keys: sidebar/nav gating + list-endpoint checks for
  // client-profile callers. Admin roles hold every page by default (parity:
  // these roles see the full nav today).
  CODE_PERMISSIONS.pageAccess.viewDashboard.key,
  CODE_PERMISSIONS.pageAccess.viewAccounts.key,
  CODE_PERMISSIONS.pageAccess.viewPipeline.key,
  CODE_PERMISSIONS.pageAccess.viewMembers.key,
  CODE_PERMISSIONS.pageAccess.viewBrokers.key,
  CODE_PERMISSIONS.pageAccess.viewDocuments.key,
  CODE_PERMISSIONS.pageAccess.viewResources.key,
  CODE_PERMISSIONS.pageAccess.viewTraining.key,
  CODE_PERMISSIONS.pageAccess.viewCommunications.key,
  CODE_PERMISSIONS.pageAccess.viewQuickProposal.key,
  CODE_PERMISSIONS.pageAccess.viewInformedAnalysis.key,
  CODE_PERMISSIONS.pageAccess.viewUserManagement.key,
  CODE_PERMISSIONS.pageAccess.viewImports.key,
  // Action-level keys enforced server-side for client-profile callers
  // (accountsRouter, clientBrokerRouter, vba membersRouter,
  // user-management-router, clientMessageRouter). Admin roles hold them all
  // so nothing regresses at ship time.
  CODE_PERMISSIONS.accounts.create.key,
  CODE_PERMISSIONS.accounts.editBasic.key,
  CODE_PERMISSIONS.accounts.movePipelineStageManual.key,
  CODE_PERMISSIONS.accounts.archive.key,
  CODE_PERMISSIONS.accounts.export.key,
  CODE_PERMISSIONS.accounts.uploadDocuments.key,
  CODE_PERMISSIONS.brokers.add.key,
  CODE_PERMISSIONS.brokers.editProfile.key,
  CODE_PERMISSIONS.brokers.editTree.key,
  CODE_PERMISSIONS.brokers.archive.key,
  ...memberActionPermissions,
  CODE_PERMISSIONS.userManagement.inviteUser.key,
  CODE_PERMISSIONS.userManagement.editUserBasic.key,
  CODE_PERMISSIONS.userManagement.editGroupAccess.key,
  CODE_PERMISSIONS.userManagement.archiveUser.key,
  CODE_PERMISSIONS.userManagement.generateRegistrationCodes.key,
  CODE_PERMISSIONS.userManagement.revokeRegistrationCodes.key,
  CODE_PERMISSIONS.governance.sendBroadcastAnnouncement.key,
];

/**
 * System role catalog. Every entry is upserted as `source = SYSTEM` by the
 * boot-time syncer (`apps/api/src/lib/syncRoles.ts`) and protected from
 * destructive admin edits — only the syncer can flip a SYSTEM role to
 * `isDeprecated` (by removing its catalog entry).
 *
 * The `permissions` array drives `RolePermission` edge reconciliation in the
 * sync — but only while the role is uncustomized. An admin's first edit to a
 * SYSTEM role's permissions through the UI marks it `isCustomized`, after
 * which the syncer leaves its edges alone permanently: admin additions and
 * removals are durable, and catalog changes here no longer flow to that role.
 *
 * Adding a system role:
 *   1. Add an entry below under the appropriate scope group.
 *   2. Add a matching string to `ROLES` so the string-to-key lookup keeps
 *      working until Phase 7 cleanup retires the legacy const.
 *   3. The next API boot upserts the row and sets its permission edges.
 */
export const SYSTEM_ROLES = {
  internal: {
    admin: {
      key: ROLES.INTERNAL_ADMIN,
      name: 'Internal admin',
      scope: 'INTERNAL',
      description: 'Full cross-tenant administrative access for CBS staff.',
      permissions: internalAdminPermissions,
    },
    accounting: {
      key: ROLES.INTERNAL_ACCOUNTING,
      name: 'Internal accounting',
      scope: 'INTERNAL',
      description:
        'Internal accounting staff. Holds the internal baseline (eligibility + CBS queue) plus carrier invoice reconciliation and the audit dashboards.',
      permissions: [
        ...internalBaselinePermissions,
        CODE_PERMISSIONS.carrierReconciliation.manage.key,
        // Pre-permissions every internal profile saw the Audit section, so
        // accounting keeps it for parity — trim per role in the admin UI.
        CODE_PERMISSIONS.audit.view.key,
      ],
    },
    analyst: {
      key: ROLES.INTERNAL_ANALYST,
      name: 'Internal analyst',
      scope: 'INTERNAL',
      description:
        'Internal data/analyst staff. Holds the internal baseline (eligibility + CBS queue) plus the audit dashboards.',
      permissions: [...internalBaselinePermissions, CODE_PERMISSIONS.audit.view.key],
    },
  },
  client: {
    admin: {
      key: ROLES.CLIENT_ADMIN,
      name: 'Client admin',
      scope: 'CLIENT',
      description:
        'Top-level admin for a client tenant. Procedure-level access via `clientProcedure`; tenant-scoping is enforced in handler code.',
      permissions: clientAdminPermissions,
    },
    eligibility: {
      key: ROLES.CLIENT_ELIGIBILITY,
      name: 'Client eligibility',
      scope: 'CLIENT',
      description: 'Eligibility-focused client staff.',
      permissions: [
        ...eligibilityPermissions,
        ...memberPhiPermissions,
        ...memberActionPermissions,
        // Member search is this role's core page; the list endpoint gates on it.
        CODE_PERMISSIONS.pageAccess.viewMembers.key,
      ],
    },
    accounting: {
      key: ROLES.CLIENT_ACCOUNTING,
      name: 'Client accounting',
      scope: 'CLIENT',
      description: 'Accounting-focused client staff.',
      permissions: [],
    },
  },
  group: {
    admin: {
      key: ROLES.GROUP_ADMIN,
      name: 'Group admin',
      scope: 'GROUP',
      description:
        'Top-level admin for a group/employer. Procedure-level access via `groupProcedure`.',
      // View-only on eligibility by design: group admins can see eligibility
      // data for their groups but only eligibility-role users may edit it.
      permissions: [
        CODE_PERMISSIONS.eligibility.view.key,
        ...memberPhiPermissions,
        CODE_PERMISSIONS.notifications.groupAdminLevel.key,
        CODE_PERMISSIONS.notifications.manageOwn.key,
      ],
    },
    eligibility: {
      key: ROLES.GROUP_ELIGIBILITY,
      name: 'Group eligibility',
      scope: 'GROUP',
      description: 'Eligibility-focused group staff.',
      permissions: [...eligibilityPermissions, ...memberPhiPermissions],
    },
    accounting: {
      key: ROLES.GROUP_ACCOUNTING,
      name: 'Group accounting',
      scope: 'GROUP',
      description: 'Accounting-focused group staff.',
      permissions: [],
    },
  },
} as const satisfies Record<string, Record<string, SystemRoleDefinition>>;

type RoleCatalog = typeof SYSTEM_ROLES;

/**
 * Compile-time union of every system role key.
 */
export type SystemRoleKey = {
  [Group in keyof RoleCatalog]: {
    [Item in keyof RoleCatalog[Group]]: RoleCatalog[Group][Item] extends {key: infer K} ? K : never;
  }[keyof RoleCatalog[Group]];
}[keyof RoleCatalog];

/**
 * Flat list of every system role definition. Consumed by the boot-time
 * syncer and the role admin UI.
 */
export const ALL_SYSTEM_ROLES: readonly SystemRoleDefinition[] = Object.values(
  SYSTEM_ROLES,
).flatMap((group) => Object.values(group));

/**
 * Transitional role-key → permission-keys map. Permission resolution is
 * DB-backed (role→permission edges via `roleId`); this map is only the
 * fallback for profiles not yet backfilled with a `roleId` (see
 * `@api/lib/permissionResolution`) and for stale frontend sessions
 * predating the session `permissions` field. Remove once the backfill
 * reports zero unmapped profiles in every environment.
 *
 * Unknown role strings (e.g. drifted `internal:admin-rfc` legacy values) map
 * to nothing; `getPermissionsForRoles` is defensive about that.
 *
 * `internal:admin-cbs` is the retired predecessor of `internal:admin` (whose
 * bundle now includes everything admin-cbs held, e.g. cbsRfcHandoff). Profiles
 * still carrying that string with a null `roleId` resolve through this map, so
 * keep the alias until the backfill/Phase 7 cleanup clears those rows —
 * without it those admins would resolve zero permissions.
 */
export const ROLE_PERMISSIONS: Readonly<Record<string, readonly CodePermissionKey[]>> = {
  ...Object.fromEntries(ALL_SYSTEM_ROLES.map((r) => [r.key, r.permissions])),
  'internal:admin-cbs': internalAdminPermissions,
};

/**
 * Resolve the union of permissions granted by a set of role strings.
 * Unknown role strings contribute no permissions.
 */
export function getPermissionsForRoles(
  roles: ReadonlyArray<Role | string | null | undefined>,
): ReadonlySet<string> {
  const result = new Set<string>();
  for (const role of roles) {
    if (!role) continue;
    const granted = ROLE_PERMISSIONS[role];
    if (!granted) continue;
    for (const permission of granted) {
      result.add(permission);
    }
  }
  return result;
}
